import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, ArrowRight, Recycle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        throw new Error('Invalid credentials');
      }
      if (!res.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_name', data.name);
      navigate('/admin');
    } catch (err) {
      // Catch any error and show a user-friendly message
      const errorMsg = err.message.includes('Failed to execute') 
        ? 'Invalid credentials' 
        : err.message || 'Login failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#060d1b] flex relative overflow-hidden">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-[#0a1628] to-[#0d1f3c] items-center justify-center p-12">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%234fd1c5\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#4fd1c5] flex items-center justify-center">
              <Recycle className="w-6 h-6 text-[#0a1628]" />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold tracking-tight">Grenew</h2>
              <p className="text-[#4fd1c5]/60 text-xs font-medium tracking-widest uppercase">Admin Portal</p>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Manage your<br />
            <span className="text-[#4fd1c5]">e-waste operations</span>
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            Access your dashboard to manage blog content, track pickup requests, monitor partner inquiries, and keep your news feed fresh.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4">
            {[
              { label: 'Content', desc: 'Blog & News' },
              { label: 'Leads', desc: 'Submissions' },
              { label: 'Analytics', desc: 'Overview' },
            ].map((item) => (
              <div key={item.label} className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                <p className="text-white text-xs font-semibold">{item.label}</p>
                <p className="text-gray-500 text-[10px] mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#4fd1c5] flex items-center justify-center">
              <Recycle className="w-5 h-5 text-[#0a1628]" />
            </div>
            <div>
              <h2 className="text-white text-lg font-bold">Grenew</h2>
              <p className="text-gray-500 text-[10px] tracking-widest uppercase">Admin Portal</p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-1">Welcome back</h1>
            <p className="text-gray-500 text-sm">Sign in to your admin account</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <div>
              <label className="block text-gray-400 text-xs font-medium mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="admin-input-username"
                className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 focus:bg-white/[0.06] transition-all"
                placeholder="Enter username"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-input-password"
                className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 focus:bg-white/[0.06] transition-all"
                placeholder="Enter password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#4fd1c5] hover:bg-[#3ec4b8] text-[#0a1628] px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 group"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              )}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-gray-600 text-xs mt-8">Grenew FZE - Content Management System</p>
        </div>
      </div>
    </main>
  );
}
